import { urlForImage } from '@app/sanity/lib/utils';
import type { ComparableBoatData, StoredBoat } from './useCompare';

export type ComparableBoatGalleryImage = {
  key: string;
  src: string;
  alt: string;
};

/**
 * The photos of one compared boat: its cover image first, then its gallery `files`.
 *
 * The same two sources, in the same order, as the boat detail page's `BoatGallery` — a compared
 * boat and a viewed boat are the same document, so they should not offer different pictures.
 *
 * `boat` is the fetched Sanity document and may not have arrived yet; `storedBoat` is what Compare
 * kept in localStorage, and its cover image is the fallback that lets a card draw something on the
 * first paint. Entries that resolve to no URL are dropped rather than rendered as broken images.
 */
export const getComparableBoatGallery = (
  boat: ComparableBoatData | undefined,
  storedBoat: StoredBoat | undefined,
  boatName: string,
): ComparableBoatGalleryImage[] => {
  const images: ComparableBoatGalleryImage[] = [];

  const coverSrc = urlForImage(boat?.image)?.url() ?? urlForImage(storedBoat?.attributes?.image)?.url();

  if (coverSrc) {
    images.push({
      key: 'cover',
      src: coverSrc,
      alt: boat?.image?.alt ?? storedBoat?.attributes?.image?.alt ?? boatName,
    });
  }

  for (const file of boat?.files ?? []) {
    const src = urlForImage(file)?.url();

    if (src) {
      images.push({ key: file._key, src, alt: file.alt ?? boatName });
    }
  }

  return images;
};
